<template>
    <article class="cta-block-container">
        <div
            class="cta-block"
            :class="{
                'cta-block--shadow': props.shadow,
            }"
        >
            <div class="cta-block__main">
                <div
                    v-if="props.heading || props.subheading"
                    class="cta-block__main-content"
                >
                    <WatsonAtomDynamicText
                        :text="props.heading"
                        :typography="props.typography.heading"
                        class="cta-block__main-heading"
                    />
                    <WatsonAtomDynamicText
                        :text="props.subheading"
                        :typography="props.typography.subheading"
                        class="cta-block__main-subheading"
                    />
                </div>

                <ul
                    v-if="items.length"
                    class="cta-block__list"
                >
                    <li
                        v-for="(item, index) in items"
                        :key="index"
                        class="cta-block__item"
                    >
                        <WatsonAtomImage
                            v-if="item.image"
                            :content="item.image"
                            class="cta-block__item-image"
                        />
                        <!-- eslint-disable vue/no-v-html -->
                        <div
                            class="cta-block__item-text"
                            v-html="item.text"
                        ></div>
                    </li>
                </ul>

                <div
                    v-if="!props.buttonHide || !props.microcopyHide"
                    class="cta-block__main__cta"
                >
                    <a
                        v-if="!props.buttonHide"
                        class="cta-block__main__button u-heading-6 u-bold"
                        @click="scrollToSelector('.content', -100 )"
                    >
                        <WatsonAtomDynamicText
                            :text="props.button"
                            :typography="props.typography.button"
                        />
                    </a>

                    <div
                        v-if="!props.microcopyHide"
                        class="cta-block__main__microcopy"
                    >
                        <WatsonAtomImage
                            v-if="props.microcopyIcon"
                            :content="props.microcopyIcon"
                            class="cta-block__main__microcopy-icon"
                        />
                        <div
                            class="cta-block__main__microcopy-text u-tiny-text"
                            v-html="props.microcopyText"
                        ></div>
                    </div>
                </div>
            </div>

            <div class="cta-block__contact">
                <WatsonAtomImage
                    v-if="props.contactImage.url"
                    :content="props.contactImage"
                    class="cta-block__contact-image"
                />

                <div
                    v-if="props.contactHeading || props.contactText"
                    class="cta-block__contact-content"
                >
                    <!-- eslint-disable vue/no-v-html -->
                    <div
                        v-if="props.contactHeading"
                        class="cta-block__contact-heading u-heading-6 u-bold"
                        v-html="props.contactHeading"
                    ></div>
                    <!-- eslint-disable vue/no-v-html -->
                    <div
                        v-if="props.contactText"
                        class="cta-block__contact-text"
                        v-html="props.contactText"
                    ></div>
                </div>
                <a
                    v-if="props.contactButton"
                    class="cta-block__contact-button u-bold"
                    :href="`tel:${props.contactButton}`"
                >
                    <WatsonAtomImage
                        v-if="props.contactButtonImage"
                        :content="props.contactButtonImage"
                        class="cta-block__contact-button-image"
                    />
                    <WatsonAtomDynamicText
                        :text="props.contactButton"
                        :typography="props.typography.contactButton"
                    />
                </a>
            </div>
        </div>
    </article>
</template>

<script setup>

import WatsonAtomImage from '@atoms/image/watson-atom-image.vue';
import WatsonAtomDynamicText from '@atoms/dynamic-text/watson-atom-dynamic-text.vue';
import { scrollToSelector } from '../../../../js/utils/scroll-to-selector';

const props = defineProps({
    content: {
        type: Object,
        required: false,
        default: () => ({}),
    },
    heading: {
        type: String,
        required: true,
    },
    subheading: {
        type: String,
        required: true,
    },
    items: {
        type: Array,
        required: true,
    },
    buttonHide: {
        type: Boolean,
        required: true,
    },
    button: {
        type: String,
        required: true,
    },
    microcopyHide: {
        type: Boolean,
        required: true,
    },
    microcopyIcon: {
        type: [Object, Boolean],
        required: false,
        default: null,
    },
    microcopyText: {
        type: String,
        required: true,
    },
    contactImage: {
        type: [Object, Boolean],
        required: false,
        default: null,
    },
    contactHeading: {
        type: String,
        required: true,
    },
    contactText: {
        type: String,
        required: true,
    },
    contactButton: {
        type: String,
        required: true,
    },
    contactButtonImage: {
        type: [Object, Boolean],
        required: false,
        default: null,
    },
    shadow: {
        type: Boolean,
        required: true,
    },
    background: {
        type: String,
        required: false,
        default: null,
    },
    contactBackground: {
        type: String,
        required: false,
        default: null,
    },
    contentColor: {
        type: String,
        required: false,
        default: null,
    },
    buttonBackground: {
        type: String,
        required: false,
        default: null,
    },
    buttonHoverColor: {
        type: String,
        required: false,
        default: null,
    },
    buttonTextColor: {
        type: String,
        required: false,
        default: null,
    },
    microcopyTextColor: {
        type: String,
        required: false,
        default: null,
    },
    contactTextColor: {
        type: String,
        required: false,
        default: null,
    },
    contactButtonBorderColor: {
        type: String,
        required: false,
        default: null,
    },
    contactButtonTextColor: {
        type: String,
        required: false,
        default: null,
    },
    typography: {
        type: Object,
        required: false,
        default: () => ({}),
    },
});
</script>

<style lang="scss" scoped>
@use 'watson-theme-cta-block';

@layer theme {
    .cta-block {
        --background: v-bind('props.background');
        --contact-background: v-bind('props.contactBackground');
        --content-color: v-bind('props.contentColor');
        --button-background: v-bind('props.buttonBackground');
        --button-hover-color: v-bind('props.buttonHoverColor');
        --button-text-color: v-bind('props.buttonTextColor');
        --microcopy-text-color: v-bind('props.microcopyTextColor');
        --contact-text-color: v-bind('props.contactTextColor');
        --contact-button-border-color: v-bind('props.contactButtonBorderColor');
        --contact-button-text-color: v-bind('props.contactButtonTextColor');
    }
}
</style>
